import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import InputBox from "../../../../FormElement/InputBox";
import { useDefaultCategories } from "../../hooks";

import "./styles.scss";

export interface ICategoryPickerProps {
  onChange: (categories: string[]) => void;
}

export const CategoryPicker: React.FunctionComponent<ICategoryPickerProps> = ({
  onChange,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`internalToolsHomePage.gbp_places_query.form.${key}`);

  const { data: defaultCategories } = useDefaultCategories();

  const [checkedDefaults, setCheckedDefaults] = useState<string[]>([]);
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState<string>("");

  useEffect(() => {
    const merged = Array.from(
      new Set(
        [...checkedDefaults, ...customCategories]
          .map((category) => category.trim())
          .filter((category) => category !== "")
      )
    );
    onChange(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedDefaults, customCategories]);

  const handleCheckCategory = (category: string, checked: boolean) => {
    setCheckedDefaults((current) =>
      checked
        ? [...current, category]
        : current.filter((item) => item !== category)
    );
  };

  const handleSelectAll = () => setCheckedDefaults(defaultCategories ?? []);
  const handleDeselectAll = () => setCheckedDefaults([]);

  const handleAddCustomCategory = () => {
    const category = newCategory.trim();
    if (category === "" || customCategories.includes(category)) {
      setNewCategory("");
      return;
    }
    setCustomCategories((current) => [...current, category]);
    setNewCategory("");
  };

  const handleRemoveCustomCategory = (category: string) => {
    setCustomCategories((current) =>
      current.filter((item) => item !== category)
    );
  };

  return (
    <div className="category-picker">
      <div className="category-picker-header">
        <div className="label-txt">{t("categories_label")}</div>
        <div className="category-picker-header-buttons">
          <button
            type="button"
            className="btn btn-border"
            onClick={handleSelectAll}
          >
            {t("categories_select_all")}
          </button>
          <button
            type="button"
            className="btn btn-border btn-border-red"
            onClick={handleDeselectAll}
          >
            {t("categories_deselect_all")}
          </button>
        </div>
      </div>
      <div className="category-picker-default-list">
        {(defaultCategories ?? []).map((category) => {
          const isSelected = checkedDefaults.includes(category);
          return (
            <button
              key={category}
              type="button"
              className={`category-picker-chip ${isSelected ? "selected" : ""}`}
              aria-pressed={isSelected}
              onClick={() => handleCheckCategory(category, !isSelected)}
            >
              {category}
            </button>
          );
        })}
      </div>
      <div className="category-picker-custom">
        <div className="label-txt">{t("categories_custom_label")}</div>
        {customCategories.map((category) => (
          <div className="category-picker-custom-item" key={category}>
            <span>{category}</span>
            <button
              type="button"
              className="btn btn-border btn-border-red"
              onClick={() => handleRemoveCustomCategory(category)}
            >
              {t("categories_remove")}
            </button>
          </div>
        ))}
        <div className="category-picker-custom-input">
          <InputBox
            value={newCategory}
            placeholder={t("categories_custom_placeholder")}
            onChange={setNewCategory}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddCustomCategory();
              }
            }}
          />
          <button
            type="button"
            className="btn btn-border"
            onClick={handleAddCustomCategory}
          >
            {t("categories_add")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryPicker;
